import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Skull, Users, MapPin, Search, MessageCircle, Brain, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type GamePhase = 'landing' | 'setup' | 'scenario' | 'investigation' | 'interrogation' | 'accusation' | 'reveal';

interface Player {
  id: string;
  name: string;
  character?: string;
  isAlive: boolean;
}

const characters = [
  { name: 'Colonel Moutarde', emoji: '🎖️', color: 'text-yellow-400', description: 'Militaire à la retraite, tempérament explosif' },
  { name: 'Professeur Violet', emoji: '🎓', color: 'text-purple-400', description: 'Scientifique brillant mais mystérieux' },
  { name: 'Mademoiselle Rose', emoji: '🌸', color: 'text-pink-400', description: 'Actrice célèbre au passé trouble' },
  { name: 'Madame Pervenche', emoji: '💎', color: 'text-blue-400', description: 'Aristocrate fortunée et manipulatrice' },
  { name: 'Révérend Olive', emoji: '📿', color: 'text-green-400', description: 'Homme d\'église aux secrets inavouables' },
  { name: 'Docteur Orchidée', emoji: '🔬', color: 'text-fuchsia-400', description: 'Médecin légiste aux méthodes douteuses' },
];

const rooms = [
  { name: 'Bibliothèque', emoji: '📚' },
  { name: 'Salon', emoji: '🛋️' },
  { name: 'Cuisine', emoji: '🍳' },
  { name: 'Salle de bal', emoji: '💃' },
  { name: 'Véranda', emoji: '🌿' },
  { name: 'Bureau', emoji: '🖊️' },
  { name: 'Cave', emoji: '🍷' },
  { name: 'Chambre', emoji: '🛏️' },
];

const weapons = [
  { name: 'Chandelier', emoji: '🕯️' },
  { name: 'Poignard', emoji: '🗡️' },
  { name: 'Revolver', emoji: '🔫' },
  { name: 'Corde', emoji: '🪢' },
  { name: 'Clé anglaise', emoji: '🔧' },
  { name: 'Poison', emoji: '☠️' },
];

export default function MurderMystery() {
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const addPlayer = () => {
    if (playerName.trim() && players.length < 6) {
      setPlayers([...players, { 
        id: crypto.randomUUID(), 
        name: playerName.trim(),
        isAlive: true
      }]);
      setPlayerName('');
    }
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  const startGame = async () => {
    setIsGenerating(true);
    // Assign random characters to players
    const shuffledCharacters = [...characters].sort(() => Math.random() - 0.5);
    const assignedPlayers = players.map((player, index) => ({
      ...player,
      character: shuffledCharacters[index]?.name
    }));
    setPlayers(assignedPlayers);
    
    // Simulate loading
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsGenerating(false);
    setPhase('scenario');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-red-950/20 to-slate-950 text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-600/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Retour</span>
        </Link>
        <div className="flex items-center gap-2">
          <Skull className="w-6 h-6 text-red-400" />
          <span className="font-bold text-red-400">Murder Mystery</span>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        
        {/* Landing */}
        {phase === 'landing' && (
          <div className="text-center space-y-8 animate-fade-in">
            <div className="space-y-4">
              <Skull className="w-24 h-24 mx-auto text-red-400 animate-pulse" />
              <h1 className="text-5xl font-black tracking-tight bg-gradient-to-r from-red-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                MURDER MYSTERY
              </h1>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Un meurtre a été commis au manoir. Enquêtez, interrogez et découvrez le coupable grâce à l'IA.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Search className="w-8 h-8 text-red-400 mb-2" />
                <h3 className="font-bold text-red-300">Investigation</h3>
                <p className="text-sm text-slate-400">Indices générés par IA</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <MessageCircle className="w-8 h-8 text-red-400 mb-2" />
                <h3 className="font-bold text-red-300">Interrogatoires</h3>
                <p className="text-sm text-slate-400">Questionnez les suspects</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <MapPin className="w-8 h-8 text-red-400 mb-2" />
                <h3 className="font-bold text-red-300">8 Pièces</h3>
                <p className="text-sm text-slate-400">Explorez le manoir</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Brain className="w-8 h-8 text-red-400 mb-2" />
                <h3 className="font-bold text-red-300">Déduction</h3>
                <p className="text-sm text-slate-400">Accusez le coupable</p>
              </div>
            </div>

            <Button 
              onClick={() => setPhase('setup')}
              className="bg-gradient-to-r from-red-500 to-purple-500 hover:from-red-600 hover:to-purple-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-red-500/25"
            >
              <Skull className="w-5 h-5 mr-2" />
              Commencer l'enquête
            </Button>
          </div>
        )}

        {/* Setup */}
        {phase === 'setup' && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-red-400" />
              <h2 className="text-3xl font-bold">Les Suspects</h2>
              <p className="text-slate-400">3 à 6 joueurs incarneront les suspects</p>
            </div>

            <div className="flex gap-2">
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPlayer()}
                placeholder="Nom du joueur..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                maxLength={20}
              />
              <Button 
                onClick={addPlayer} 
                disabled={!playerName.trim() || players.length >= 6}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Ajouter
              </Button>
            </div>

            <div className="space-y-2">
              {players.map((player, index) => (
                <div 
                  key={player.id}
                  className="flex items-center justify-between bg-slate-800/50 px-4 py-3 rounded-lg border border-slate-700/50"
                >
                  <span className="flex items-center gap-3">
                    <span className="text-2xl">{characters[index]?.emoji}</span>
                    <div>
                      <p className="font-medium">{player.name}</p>
                      <p className={`text-xs ${characters[index]?.color}`}>{characters[index]?.name}</p>
                    </div>
                  </span>
                  <button 
                    onClick={() => removePlayer(player.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {players.length < 3 && (
              <p className="text-center text-slate-500 text-sm">
                Minimum 3 suspects ({3 - players.length} restant{3 - players.length > 1 ? 's' : ''})
              </p>
            )}

            <Button 
              onClick={startGame}
              disabled={players.length < 3 || isGenerating}
              className="w-full bg-gradient-to-r from-red-500 to-purple-500 hover:from-red-600 hover:to-purple-600 text-white font-bold py-6 text-lg rounded-xl"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Préparation du meurtre...
                </>
              ) : (
                <>
                  <Skull className="w-5 h-5 mr-2" />
                  Lancer l'enquête
                </>
              )}
            </Button>
          </div>
        )}

        {/* Scenario */}
        {phase === 'scenario' && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm">
                <Sparkles className="w-4 h-4" />
                Prototype en développement
              </div>
              <h2 className="text-3xl font-bold text-red-400">Le Crime</h2>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-red-500/30 space-y-4">
              <div className="text-center">
                <p className="text-6xl mb-4">💀</p>
                <h3 className="text-xl font-bold text-red-300 mb-2">Un corps a été découvert !</h3>
                <p className="text-slate-300">
                  Lord Blackwood a été retrouvé mort dans son manoir lors de sa soirée annuelle.
                  Tous les invités sont suspects...
                </p>
              </div>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 space-y-4">
              <h3 className="font-semibold text-purple-400 mb-3">🎭 Personnages attribués</h3>
              <div className="grid grid-cols-2 gap-3">
                {players.map((player) => {
                  const char = characters.find(c => c.name === player.character);
                  return (
                    <div key={player.id} className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/50">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{char?.emoji}</span>
                        <div>
                          <p className="font-medium text-sm">{player.name}</p>
                          <p className={`text-xs ${char?.color}`}>{char?.name}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-500/10 to-purple-500/10 p-6 rounded-xl border border-red-500/30 text-center">
              <h3 className="font-semibold text-red-300 mb-3">🚧 Fonctionnalités à venir</h3>
              <ul className="text-sm text-slate-400 space-y-2">
                <li>• Génération de scénarios uniques par IA</li>
                <li>• Système d'indices et de preuves</li>
                <li>• Interrogatoires dynamiques</li>
                <li>• Phase d'accusation et révélation</li>
                <li>• Narration vocale immersive</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <h4 className="font-semibold text-slate-300 mb-2">🏠 Pièces</h4>
                <div className="flex flex-wrap gap-2">
                  {rooms.slice(0, 4).map(room => (
                    <span key={room.name} className="text-xl" title={room.name}>{room.emoji}</span>
                  ))}
                </div>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <h4 className="font-semibold text-slate-300 mb-2">🔪 Armes</h4>
                <div className="flex flex-wrap gap-2">
                  {weapons.map(weapon => (
                    <span key={weapon.name} className="text-xl" title={weapon.name}>{weapon.emoji}</span>
                  ))}
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setPhase('landing')}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
